import { RouterProvider } from '@tanstack/react-router'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'

import { createApiClient } from '~/api/client'
import { createApi } from '~/api/endpoints'
import { AuthProvider } from '~/auth/auth-context'
import { clearSession, loadSession } from '~/auth/session-storage'
import { config } from '~/config/env'
import { DataProvider } from '~/data/data-context'
import { createIndexedDbStore } from '~/data/indexed-db-store'
import { createDataService } from '~/data/service'
import { registerServiceWorker } from '~/lib/service-worker'
import { createAppRouter } from '~/router'
import '~/styles/app.css'

const foundRoot = document.getElementById('root')

if (!foundRoot) {
  throw new Error('Expected an element with id "root" in index.html')
}

const rootElement = foundRoot

const getToken = () => loadSession()?.token ?? null

const client = createApiClient({
  baseUrl: config.apiBaseUrl,
  getToken,
  onUnauthorized: clearSession,
})
const api = createApi({
  client,
  baseUrl: config.apiBaseUrl,
  getToken,
})
const service = createDataService({
  api,
  store: createIndexedDbStore(),
})
const router = createAppRouter()

async function start() {
  // The device's cache renders first; the server catches it up right after.
  await service.init()

  createRoot(rootElement).render(
    <StrictMode>
      <AuthProvider api={api} service={service} initialSession={loadSession()}>
        <DataProvider service={service} api={api}>
          <RouterProvider router={router} />
        </DataProvider>
      </AuthProvider>
    </StrictMode>,
  )

  void service.refresh()

  // Last, and only in production: what it holds is what makes the next visit quick, and none
  // of it is anything this visit is waiting on.
  registerServiceWorker()
}

void start()
